import { useAppNotifications } from "@/hooks/useAppNotifications";
import FullScreenLoading from "@/components/FullScreenLoading";
import GenericErrorTemplate from "@/components/GenericErrorTemplate";
import { ROUTE_PARAMS, TOAST_AUTO_HIDE_DURATION } from "@/constants";
import { useQuery } from "@/hooks/apollo/useQuery";
import {
  getIamAccountIdFromUserToken,
  signInWithAccountContext,
} from "@/provider/AuthProvider/accountAuthorize";
import {
  applyAccountIdAuthPolicyForRole,
  shouldSkipAccountIdAuthorize,
} from "@/provider/AuthProvider/accountIdAuthPolicy";
import { rememberAccounts } from "@/store/rememberedAccountsStorage";
import { setCurrentIamAccountId } from "@/store/dxpIamAuthHeader";
import { useDealerAccountIdsStore } from "@/store/useDealerAccountIdsStore";
import { useRouteDealerCdhAccountId } from "@/hooks/useRouteDealerCdhAccountId";
import { pageLayout } from "@/theme/pageLayout";
import { isAuthCallbackPath } from "@/utils/silentTokenRefresh";
import { Box } from "@mui/material";
import { useEffect, useRef } from "react";

import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import {
  generatePath,
  matchPath,
  useLocation,
  useNavigate,
  useParams,
} from "react-router";
import { ME } from "@/context/me/MeProvider";
import { accountsV2Query } from "./accountsV2Query";
import { AccountContext } from "./AccountContext";
import type { Account } from "./AccountContext";
import { mapAccountsV2 } from "./mapAccountSummary";
import { authSignOutRedirect } from "@/provider/AuthProvider/signOut";
import { useSupportMode } from "@/context/supportMode";
import { isActiveSupportModeForRouteAccount } from "@/context/supportMode/supportModeAuthHeaders";
import {
  findDealerAccountByRouteSegment,
  getDealerAccountRouteId,
} from "./dealerAccountRouteId";

const ACCOUNT_SECTION_ROOTS = new Set([
  "audit-logs",
  "contracts",
  "customers",
  "dealer",
  "dealer-members",
  "devices",
  "invoices",
  "settings",
]);

/** Account-scoped error pages — never preserve these when switching accounts. */
const ACCOUNT_ERROR_PAGE_ROOTS = new Set<string>([
  ROUTE_PARAMS.ACCESS_DENIED,
  ROUTE_PARAMS.FILE_NOT_FOUND,
]);

const generateAccountPath = (pathname: string, newAccountId: string) => {
  const match = matchPath(`/:${ROUTE_PARAMS.ACCOUNT_ID}/*`, pathname);
  let subpath = match?.params["*"] ?? "";

  const trimmedSubpath = subpath.replace(/^\/+|\/+$/g, "");
  if (trimmedSubpath !== "") {
    const [sectionRoot] = trimmedSubpath.split("/");
    if (
      sectionRoot !== undefined &&
      ACCOUNT_ERROR_PAGE_ROOTS.has(sectionRoot)
    ) {
      subpath = "";
    } else if (
      sectionRoot !== undefined &&
      ACCOUNT_SECTION_ROOTS.has(sectionRoot)
    ) {
      subpath = sectionRoot;
    }
  }

  return generatePath(`/:${ROUTE_PARAMS.ACCOUNT_ID}/*`, {
    [ROUTE_PARAMS.ACCOUNT_ID]: newAccountId,
    "*": subpath,
  });
};

type AccountProviderProps = {
  children?: React.ReactNode;
};

export const AccountProvider = (props: AccountProviderProps) => {
  const { children } = props;

  const { [ROUTE_PARAMS.ACCOUNT_ID]: accountId } = useParams();
  const navigate = useNavigate();
  const notification = useAppNotifications();
  const { pathname, search, hash } = useLocation();
  const { signoutRedirect, signinRedirect, user } = useAuth();
  const { t } = useTranslation();
  const { session: supportModeSession } = useSupportMode();
  const signoutTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const accountContextRedirectRef = useRef(false);

  useEffect(() => {
    return () => {
      if (signoutTimeoutRef.current !== undefined) {
        clearTimeout(signoutTimeoutRef.current);
      }
    };
  }, []);

  const routeIamId =
    accountId !== undefined && accountId.length > 0 ? accountId : undefined;

  const isActiveSupportMode = isActiveSupportModeForRouteAccount(
    supportModeSession,
    routeIamId
  );
  const routeCdhAccountId = useRouteDealerCdhAccountId();
  const addDealerAccountIds = useDealerAccountIdsStore(
    (state) => state.addDealerAccountIds
  );

  const accountsQueryResult = useQuery(accountsV2Query, {
    context: { skipGlobalError: true },
    ...(isActiveSupportMode ? { fetchPolicy: "network-only" } : {}),
  });
  const meQueryResult = useQuery(ME, {
    context: { skipGlobalError: true },
    ...(isActiveSupportMode ? { fetchPolicy: "network-only" } : {}),
  });
  const meRole = meQueryResult.data?.me?.role;
  const meQuerySettled =
    !meQueryResult.loading &&
    (meQueryResult.data?.me !== undefined || meQueryResult.error !== undefined);
  const {
    loading: accountsLoading,
    error: accountsError,
    dataState: accountsDataState,
  } = accountsQueryResult;

  const accountsFromList: Account[] =
    accountsQueryResult.dataState === "complete"
      ? mapAccountsV2(accountsQueryResult.data.accountsV2)
      : [];

  useEffect(() => {
    addDealerAccountIds(
      accountsFromList.map((account) => ({
        cdhId: account.id,
        iamId: account.iamId,
      }))
    );
  }, [accountsFromList, addDealerAccountIds]);

  const getAccountPath = (iamId: string) => {
    return generateAccountPath(pathname, iamId);
  };

  const getAccountDashboardPath = (iamId: string) => {
    return generateAccountPath(pathname, iamId);
  };

  const delayedSignout = () => {
    if (signoutTimeoutRef.current !== undefined) {
      clearTimeout(signoutTimeoutRef.current);
    }
    signoutTimeoutRef.current = setTimeout(() => {
      void authSignOutRedirect(signoutRedirect);
    }, 2000);
  };

  const selectedAccount =
    routeIamId !== undefined
      ? findDealerAccountByRouteSegment(accountsFromList, routeIamId)
      : undefined;

  const supportModeAccount: Account | undefined = isActiveSupportMode
    ? {
        id: supportModeSession.cdhId,
        iamId: supportModeSession.iamAccountId,
        companyName: supportModeSession.accountName,
        primary: true,
      }
    : undefined;

  let accounts: Account[] = accountsFromList;
  if (accounts.length === 0 && supportModeAccount !== undefined) {
    accounts = [supportModeAccount];
  }

  const primaryAccount: Account | undefined =
    accounts.find((a) => a.primary) ??
    (accounts.length === 1 ? accounts[0] : undefined);

  const effectiveSelectedAccount = selectedAccount ?? supportModeAccount;
  setCurrentIamAccountId(effectiveSelectedAccount?.iamId);

  const accountsReady = !accountsLoading && accountsDataState === "complete";
  const needsRouteFix = effectiveSelectedAccount === undefined;
  const targetAccount = effectiveSelectedAccount ?? primaryAccount;
  const targetAccountId = targetAccount?.id;
  const targetIamId = targetAccount?.iamId;
  const targetRouteId =
    targetAccount !== undefined
      ? getDealerAccountRouteId(targetAccount)
      : undefined;
  const primaryAccountId = primaryAccount?.id;
  const primaryIamId = primaryAccount?.iamId;
  const tokenIamAccountId = getIamAccountIdFromUserToken(user);

  const currentPath = `${pathname}${search}${hash}`;

  const skipAccountIdAuthorize =
    shouldSkipAccountIdAuthorize(meRole) || isActiveSupportMode;

  useEffect(() => {
    if (meRole != null) {
      applyAccountIdAuthPolicyForRole(meRole);
    }
  }, [meRole]);

  const setAccount = (iamId: string) => {
    navigate(getAccountPath(iamId));
  };

  const accountsListFailed =
    accountsDataState === "complete" && accountsError !== undefined;

  const hasNoAccessToRoutedAccount =
    accountsReady &&
    primaryAccountId !== undefined &&
    needsRouteFix &&
    routeIamId !== undefined;

  useEffect(() => {
    if (accountsListFailed) {
      notification.show(t("accounts.error_loading_accounts_list"), {
        severity: "warning",
        autoHideDuration: TOAST_AUTO_HIDE_DURATION,
      });
      return;
    }

    if (hasNoAccessToRoutedAccount) {
      notification.show(t("accounts.no_access_to_this_account"), {
        severity: "error",
        autoHideDuration: TOAST_AUTO_HIDE_DURATION,
      });
    }
  }, [accountsListFailed, hasNoAccessToRoutedAccount, notification, t]);

  // The first authorize call cannot carry account_id (accounts need a token to load),
  // so the dealer's IAM account is applied to the token once the list resolves.
  useEffect(() => {
    if (
      !accountsReady ||
      !meQuerySettled ||
      targetIamId === undefined ||
      targetAccountId === undefined
    ) {
      return;
    }
    if (isAuthCallbackPath(pathname)) {
      return;
    }

    const targetPath =
      needsRouteFix && targetRouteId !== undefined
        ? `${generateAccountPath(pathname, targetRouteId)}${search}${hash}`
        : currentPath;

    if (tokenIamAccountId !== targetIamId) {
      if (skipAccountIdAuthorize) {
        if (needsRouteFix) {
          navigate(targetPath, { replace: true });
        }
        return;
      }

      if (accountContextRedirectRef.current) {
        return;
      }
      accountContextRedirectRef.current = true;
      void signInWithAccountContext(signinRedirect, targetIamId, targetPath);
      return;
    }

    if (!skipAccountIdAuthorize) {
      rememberAccounts({
        primary:
          primaryAccountId !== undefined && primaryIamId !== undefined
            ? { cdhId: primaryAccountId, iamId: primaryIamId }
            : undefined,
        last: { cdhId: targetAccountId, iamId: targetIamId },
      });
    }

    if (needsRouteFix) {
      navigate(targetPath, { replace: true });
    }
  }, [
    accountsReady,
    currentPath,
    hash,
    meQuerySettled,
    navigate,
    needsRouteFix,
    pathname,
    primaryAccountId,
    primaryIamId,
    search,
    signinRedirect,
    skipAccountIdAuthorize,
    targetAccountId,
    targetIamId,
    targetRouteId,
    tokenIamAccountId,
  ]);

  if (accountsLoading && accountsDataState === "empty") {
    return <FullScreenLoading title={t("accounts.loading")} />;
  }
  if (accountsError !== undefined && accountsDataState === "empty") {
    return (
      <Box sx={{ ...pageLayout, pt: "160px" }}>
        <GenericErrorTemplate
          header={t("something_went_wrong")}
          description={t("accounts.error_loading_accounts")}
          showDealerDashboardButton={false}
        />
      </Box>
    );
  }

  if (accounts.length === 0) {
    notification.show(t("accounts.no_accounts"), {
      severity: "error",
      autoHideDuration: TOAST_AUTO_HIDE_DURATION,
    });
    delayedSignout();
    return (
      <Box sx={{ ...pageLayout, pt: "160px" }}>
        <GenericErrorTemplate
          header={t("something_went_wrong")}
          description={t("accounts.no_accounts")}
          showDealerDashboardButton={false}
        />
      </Box>
    );
  }

  if (primaryAccount === undefined) {
    notification.show(t("accounts.no_primary_accounts"), {
      severity: "error",
      autoHideDuration: TOAST_AUTO_HIDE_DURATION,
    });
    delayedSignout();
    return <FullScreenLoading title={t("accounts.logging_out")} />;
  }

  const tokenMatchesSelectedAccount =
    tokenIamAccountId !== undefined &&
    tokenIamAccountId === effectiveSelectedAccount?.iamId;

  const mustWaitForMeRole = !meQuerySettled && !tokenMatchesSelectedAccount;

  const needsAccountContextSync =
    meQuerySettled &&
    !skipAccountIdAuthorize &&
    !tokenMatchesSelectedAccount &&
    !isAuthCallbackPath(pathname);

  // Apollo scopes the cache and `dxp-account-ids` by the CDH id resolved from the route.
  const apolloScopedToSelectedAccount =
    routeCdhAccountId !== undefined &&
    routeCdhAccountId === effectiveSelectedAccount?.id;

  if (
    mustWaitForMeRole ||
    effectiveSelectedAccount === undefined ||
    needsAccountContextSync ||
    !apolloScopedToSelectedAccount
  ) {
    return <FullScreenLoading title={t("accounts.switching_account")} />;
  }

  return (
    <AccountContext.Provider
      value={{
        accounts,
        accountsLoading,
        account: effectiveSelectedAccount,
        accountRouteId: getDealerAccountRouteId(effectiveSelectedAccount),
        setAccount,
        getAccountPath,
        getAccountDashboardPath,
      }}
      key={effectiveSelectedAccount.id}
    >
      {children}
    </AccountContext.Provider>
  );
};
